import { Alert, Button, Form, Input, List, Modal, Row, Select, Tag } from "antd";
import { buttonStyle, fullWidthStyle, titleStyle } from "../../constants/style";
import { SubmitStatus } from "../../api/work-order-api";
import ApiContext from "../../api/context";
import { useContext, useEffect, useState } from "react";
import Paragraph from "antd/es/typography/Paragraph";
import Title from "antd/es/typography/Title";
import { Template, TemplateStatusType, WorkOrder } from "../../api/models";

type WorkOrderCreateProps = {
  template?: Template,
  onClose: () => void,
  onCreate: (workOrder: WorkOrder) => void
};

type DependentFiles = {
  [filePath: string]: "CSV" | "Smart Folder"
};

export default function (p: WorkOrderCreateProps) {
  const [template, setTemplate] = useState(p.template);
  const [templates, setTemplates] = useState<Template[]>(p.template ? [p.template] : []);
  const [workOrderName, setWorkOrderName] = useState("");
  const [renderCount, setRenderCount] = useState<number>(0);
  const [initializationSubmitStatus, setInitializationSubmitStatus] = useState<SubmitStatus>(p.template ? SubmitStatus.Idle : SubmitStatus.Submitting);
  const [templateSubmitStatus, setTemplateSubmitStatus] = useState<SubmitStatus>(p.template ? SubmitStatus.Submitting : SubmitStatus.Idle);
  const [creationSubmitStatus, setCreationSubmitStatus] = useState<SubmitStatus>(SubmitStatus.Idle);
  const [error, setError] = useState<string>();
  const { api } = useContext(ApiContext);
  const isInitializationSubmitting = initializationSubmitStatus === SubmitStatus.Submitting;
  const isTemplateSubmitting = templateSubmitStatus === SubmitStatus.Submitting;
  const isCreationSubmitting = creationSubmitStatus === SubmitStatus.Submitting;
  const isSubmitting = isInitializationSubmitting || isTemplateSubmitting || isCreationSubmitting;
  const canSave = !isSubmitting &&
    template !== undefined &&
    workOrderName.length > 0 &&
    renderCount > 0 &&
    creationSubmitStatus !== SubmitStatus.Submitted;

  useEffect(() => {
    if (!isInitializationSubmitting) {
      return;
    }

    // Pull the template list if one wasn't provided.
    if (!p.template) {
      api?.getTemplates({ deleted: false, statuses: [TemplateStatusType.Ready] }).then(response => {
        if (!response.good() || !response.body) {
          throw "Failed to get the dependent file list.";
        }

        setTemplates(response.body);
      }).catch(() => {
        setError("failed to get the template list.");
      }).finally(() => {
        setInitializationSubmitStatus(SubmitStatus.Idle);
      });
    }
  }, [initializationSubmitStatus, setError, setTemplate, setInitializationSubmitStatus]);

  useEffect(() => {
    if (!isTemplateSubmitting) {
      return;
    }

    if (!template?.templateID) {
      setError("Failed to get the template.");
      setTemplateSubmitStatus(SubmitStatus.Idle);
      return;
    }

    api?.getTemplate(template!.templateID!).then(response => {
      if (!response.good() || !response.body) {
        throw "Failed to get the dependent file list.";
      }

      setTemplate(response.body);
    }).catch(() => {
      setError("failed to get the template list.");
    }).finally(() => {
      setTemplateSubmitStatus(SubmitStatus.Idle);
    });
  }, [template, templateSubmitStatus, setTemplate, setTemplateSubmitStatus]);

  const dependentFiles: DependentFiles = {};
  for (const csv of template?.csvParameters ?? []) {
    dependentFiles[csv.assetPath] = "CSV";
  }
  for (const smartFolder of template?.smartFolderParameters ?? []) {
    dependentFiles[smartFolder.assetPath] = "Smart Folder";
  }
  const dependentFileKeys = Object.keys(dependentFiles);

  let dependentFilesEmptyText = " ";
  if (isSubmitting) {
    dependentFilesEmptyText = " ";
  } else if (!template) {
    dependentFilesEmptyText = "Select a template to see its dependent parameter files.";
  } else if (dependentFileKeys.length <= 0) {
    dependentFilesEmptyText = "Selected template has no dependent parameter files.";
  }

  return (
    <>
      <Title style={titleStyle} level={4}>Create Work Order</Title>
      <Form
        layout="vertical"
        onFinish={_ => {
          setCreationSubmitStatus(SubmitStatus.Submitting);
          const workOrder: WorkOrder = {
            name: workOrderName.trim(),
            renderCount: renderCount!,
            templateID: template!.templateID!
          };

          api?.postWorkOrder(workOrder).then(workOrderResponse => {
            const isGood = workOrderResponse.good();
            if (!isGood) {
              setError("There was a problem processing the request.");
              setCreationSubmitStatus(SubmitStatus.Idle);
              return;
            }

            p.onCreate(workOrderResponse.body!);
            setCreationSubmitStatus(SubmitStatus.Submitted);
            setError(undefined);
          }).catch(() => {
            setCreationSubmitStatus(SubmitStatus.Idle);
            setError("Failed to create the work order.");
          });
        }}
        colon={false}>
        <Form.Item label="Template">
          <Select
            placeholder="Select a template"
            disabled={templates.length === 1 && template !== undefined}
            value={template?.templateID?.toString() ?? undefined}
            style={fullWidthStyle}
            loading={isInitializationSubmitting || isTemplateSubmitting}
            onSelect={e => {
              setTemplate(templates.find(t => t.templateID! === Number(e)));
              setTemplateSubmitStatus(SubmitStatus.Submitting);
            }}
            options={templates.map(t => ({
              key: t.templateID!,
              value: t.templateID!.toString(),
              label: t.name
            }))}
          />
        </Form.Item>
        <Form.Item label="Name">
          <Input
            placeholder="Enter a work order name"
            value={workOrderName}
            onChange={e => setWorkOrderName(e.target.value)}
            onBlur={() => setWorkOrderName(workOrderName.trim())}
          />
        </Form.Item>
        <Form.Item label="Render Count">
          <Input
            placeholder="Enter the render count"
            pattern="\d+"
            value={renderCount ?? 0 > 0 ? renderCount : ""}
            onChange={e => setRenderCount(Number(e.target.value))}
          />
        </Form.Item>
        <Form.Item
          label="Dependent Files">
          <List
            bordered={false}
            footer={false}
            pagination={{ pageSize: 5, hideOnSinglePage: dependentFileKeys.length < 6 }}
            size="small"
            dataSource={dependentFileKeys}
            locale={{ emptyText: dependentFilesEmptyText }}
            renderItem={(filePath: string) =>
              <List.Item key={filePath}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={fullWidthStyle}>
                  <span>{filePath}</span>
                  <Tag color={"blue"}>
                    {dependentFiles[filePath]}
                  </Tag>
                </Row>
              </List.Item>
            }
          >
          </List>
        </Form.Item>
        <Row style={fullWidthStyle} justify="end" align="middle">
          <Button
            type="default"
            style={buttonStyle}
            disabled={isCreationSubmitting}
            onClick={p.onClose}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            style={buttonStyle}
            loading={isCreationSubmitting}
            disabled={!canSave}>
            Create
          </Button>
        </Row>
        {
          !error ? undefined :
            <>
              <Paragraph></Paragraph>
              <Alert style={fullWidthStyle} type="error" title={error} />
            </>
        }
      </Form>
    </>);
}

